import {
  Modal,
  TextInput,
  Stack,
  Group,
  Button,
  Text,
  ScrollArea,
  Accordion,
  Loader,
  Center,
} from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useConfigurationTemplates } from '../../../../hooks/useConfigurationTemplates';

interface ImportTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  onImport: (templateId: string | number) => Promise<void> | void;
}

interface TemplateDisplayData {
  id: string | number;
  name: string;
  shortDescription: string;
  description: string;
}

interface AccordionLabelProps {
  label: string;
  description: string;
  isSelected: boolean;
}

function AccordionLabel({
  label,
  description,
  isSelected,
}: AccordionLabelProps) {
  return (
    <Group wrap='nowrap' style={{ flex: 1 }}>
      <div style={{ flex: 1 }}>
        <Text fw={500} c={isSelected ? 'blue' : 'inherit'}>
          {label}
        </Text>
        <Text size='sm' c='dimmed' fw={400}>
          {description}
        </Text>
      </div>
      {isSelected && (
        <Text size='xs' mr='xs' c='blue' fw={500}>
          Selezionato
        </Text>
      )}
    </Group>
  );
}

export function ImportTemplateModal({
  opened,
  onClose,
  onImport,
}: ImportTemplateModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | number | null
  >(null);
  const [expandedValue, setExpandedValue] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { templates, loading, error, refetch } = useConfigurationTemplates();

  // Carica i template quando si apre la modale
  useEffect(() => {
    if (opened) {
      refetch();
    }
  }, [opened, refetch]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!opened) {
      setSelectedTemplateId(null);
      setSearchQuery('');
      setExpandedValue(null);
    }
  }, [opened]);

  // Mappa i template dal DB ai dati per la visualizzazione
  const mappedTemplates = useMemo<TemplateDisplayData[]>(() => {
    return templates.map(template => {
      const description = template.description || '';
      const shortDescription = template.shortDescription || '';

      return {
        id: template.id,
        name: template.name,
        shortDescription,
        description,
      };
    });
  }, [templates]);

  // Filtra i template in base alla ricerca
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return mappedTemplates;
    }

    const query = searchQuery.toLowerCase();
    return mappedTemplates.filter(
      template =>
        template.name.toLowerCase().includes(query) ||
        template.shortDescription.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query)
    );
  }, [searchQuery, mappedTemplates]);

  const handleImport = async () => {
    if (!selectedTemplateId || isImporting) {
      return;
    }

    try {
      setIsImporting(true);
      await Promise.resolve(onImport(selectedTemplateId));
      onClose();
    } catch {
      // Error handling is done by the handler
    } finally {
      setIsImporting(false);
    }
  };

  const handleAccordionChange = (value: string | null) => {
    setExpandedValue(value);
    // Quando si apre un accordion, seleziona anche il template
    if (value) {
      // Converti string a number se necessario
      const template = mappedTemplates.find(t => String(t.id) === value);
      if (template) {
        setSelectedTemplateId(template.id);
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title='Importa da Template'
      centered
      size='lg'
    >
      <Stack gap='md'>
        {/* Search Input */}
        <TextInput
          placeholder='Cerca template...'
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          leftSection={<Search size={16} />}
        />

        {/* Content: Lista Template */}
        <ScrollArea h={400}>
          {loading ? (
            <Center py='xl'>
              <Loader size='md' />
            </Center>
          ) : error ? (
            <Text size='sm' c='red' ta='center' py='xl'>
              Errore nel caricamento dei template: {error}
            </Text>
          ) : filteredTemplates.length === 0 ? (
            <Text size='sm' c='dimmed' ta='center' py='xl'>
              {searchQuery.trim()
                ? 'Nessun template trovato'
                : 'Nessun template disponibile'}
            </Text>
          ) : (
            <Accordion
              value={expandedValue}
              onChange={handleAccordionChange}
              chevronPosition='right'
              variant='contained'
              radius='md'
            >
              {filteredTemplates.map(template => {
                const isSelected = selectedTemplateId === template.id;
                const templateIdString = String(template.id);
                return (
                  <Accordion.Item
                    key={templateIdString}
                    value={templateIdString}
                  >
                    <Accordion.Control aria-label={template.name}>
                      <AccordionLabel
                        label={template.name}
                        description={template.shortDescription}
                        isSelected={isSelected}
                      />
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text size='sm'>{template.description}</Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </ScrollArea>

        {/* Footer: Cancel e Import */}
        <Group justify='flex-end' gap='sm'>
          <Button variant='light' onClick={onClose}>
            Annulla
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedTemplateId || isImporting}
            loading={isImporting}
          >
            Importa
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
